import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

export interface ProductServiceData {
  _id: string;
  name: string;
  description: string;
  sku?: string;
  hsnSacId?: string;
  hsnSac?: string;
  category?: 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE';
  defaultRate: number;
  /** Minimum selling rate allowed after discount; 0/undefined = no minimum configured. */
  baseRate?: number;
  currency?: string;
  unitId?: string;
  unit?: string;
  defaultUnit?: string;
  taxMasterId?: string;
  taxability?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface HsnSacData {
  _id: string;
  code: string;
  description: string;
  type: 'HSN' | 'SAC';
  taxMasterId?: string;
  isActive: boolean;
}

export interface TaxMasterData {
  _id: string;
  /** Set by this hook's normaliser; the API sends `_id`. */
  id?: string;
  name: string;
  code?: string;
  treatment?: 'taxable' | 'nil_rated' | 'exempt' | 'non_gst';
  /** Total GST percentage. CGST / SGST are half of it and IGST is the whole, derived by the API. */
  rate: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
  isActive: boolean;
}

export interface UnitMasterData {
  _id: string;
  name: string;
  shortCode: string;
  displayName: string;
  isActive: boolean;
}

export interface PaymentTermData {
  _id: string;
  name: string;
  description: string;
  schedule: any[];
  isDefault: boolean;
  isActive: boolean;
}

export interface TermConditionData {
  _id: string;
  id?: string;
  title: string;
  content: string;
  category?: string;
  isDefault: boolean;
  displayOrder: number;
  isActive: boolean;
}

export interface AssumptionExclusionData {
  _id: string;
  id?: string;
  title: string;
  description: string;
  category: 'ASSUMPTION' | 'EXCLUSION' | 'Assumption' | 'Exclusion';
  isDefault: boolean;
  isActive: boolean;
}

export interface BankDetailData {
  id: string;
  organizationId: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  branch: string;
  ifsc: string;
  upi?: string;
  accountType?: string;
  currency?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DeclarationData {
  id: string;
  organizationId: string;
  title: string;
  content: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QuotationDefaultsData {
  id: string;
  defaultValidityDays: number;
  defaultPaymentTerms: string;
  defaultTermsAndConditions: string;
  defaultCgstRate: number;
  defaultSgstRate: number;
  defaultIgstRate: number;
  defaultCurrency: string;
  defaultGstEnabled: boolean;
  defaultPlaceOfSupply: string;
  defaultReverseCharge: boolean;
  defaultDeclarationId: string | null;
  defaultAssumptionIds: string[];
  defaultExclusionIds: string[];
  defaultTermIds: string[];
  defaultDisplaySettingId: string | null;
  // Feature toggles
  assumptionsExclusionsEnabled?: boolean;
  numberFormat: string;
  padding: number;
  fiscalYear: string;
}

export interface DocumentMasterData {
  productServices: ProductServiceData[];
  hsnSacCodes: HsnSacData[];
  taxMasters: TaxMasterData[];
  unitMasters: UnitMasterData[];
  paymentTerms: PaymentTermData[];
  termConditions: TermConditionData[];
  assumptionsExclusions: AssumptionExclusionData[];
  bankDetails: BankDetailData[];
  declarations: DeclarationData[];
  displaySettings: any[];
  quotationDefaults: QuotationDefaultsData;
}

export function useDocumentMasterData() {
  const { organization } = useAuth();
  const { can } = usePermissions();

  return useQuery({
    queryKey: ['documentMasterData', organization?.id],
    queryFn: async (): Promise<DocumentMasterData> => {
      const res = await api.get<any>('/master-data/quotation-config', {
        organizationId: organization?.id,
      });

      const d = res?.data || {};
      const norm = (arr: any[] = []) => arr.map((x: any) => {
        const out: any = { ...x, id: x._id || x.id };
        if (out.hsnSac && typeof out.hsnSac === 'object') out.hsnSac = out.hsnSac.code || out.hsnSac._id || String(out.hsnSac);
        return out;
      });

      const rawDefaults = d.quotationDefaults;
      let normedDefaults: QuotationDefaultsData = rawDefaults || {};
      if (rawDefaults && !rawDefaults.id && rawDefaults._id) {
        normedDefaults = { ...rawDefaults, id: rawDefaults._id };
      }

      return {
        productServices: norm(d.productServices || []),
        hsnSacCodes: norm(d.hsnSacCodes || []),
        taxMasters: norm(d.taxMasters || []),
        unitMasters: norm(d.unitMasters || []),
        paymentTerms: norm(d.paymentTerms || []),
        termConditions: norm(d.termConditions || []),
        assumptionsExclusions: norm(d.assumptionsExclusions || []),
        bankDetails: norm(d.bankDetails || []),
        declarations: norm(d.declarations || []),
        displaySettings: norm(d.displaySettings || []),
        quotationDefaults: normedDefaults,
      };
    },
    enabled: !!organization?.id && (can('QUOTATIONS', 'VIEW') || can('SALES_DOCS', 'VIEW')),
    staleTime: 5 * 60 * 1000,
  });
}
